import { useState } from 'react';
import { MdStar, MdStarOutline } from 'react-icons/md';
import { Chip, IconButton } from '@ds';
import { CaseCardType } from '@lp/features/CasesBoard/components/CaseCard/constants';
import { CardTitle } from '@features/components/CaseCard/Blocks/Base/CardTitle';
import { CardHospital } from '@features/components/CaseCard/Blocks/Base/CardHospital';
import { getCaseStatusName, getColorByStatus, getCaseSuffixes } from '@/utils/helpers';
import { Case } from '@/api/apis/case';
import { useAddCaseToFavorite, useRemoveCaseFromFavorite } from '@/queries/case';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { BaseCardActions } from './BaseCardActions';
import { Location } from '@/api/types';

type BaseCaseInfoProps = {
  cardType: CaseCardType;
  caseData: Case;
  dateValue: string;
  dateLabel?: string;
  color: string;
  isReadOnly?: boolean;
  selectType?: 'single' | 'multiple';
  openResources?: () => void;
};

export const BaseCaseInfo = ({
  cardType,
  caseData,
  color,
  dateLabel,
  dateValue,
  openResources,
  isReadOnly = false,
  selectType = 'single',
}: BaseCaseInfoProps) => {
  const isCandidateCard = cardType === CaseCardType.Candidate;
  const {
    caseId,
    status,
    donorType,
    organType,
    isFavourite,
    donorHospitalDetails: donorHospital,
    txCenterDetails: recipientHospital,
    serviceType,
    randomization,
    donorId,
    reAllocationSequenceNumber,
    opoRequest,
    isExpeditedCase,
    laterality,
    duplicateCaseNumber,
  } = caseData;
  const { mutateAsync: addFavorite } = useAddCaseToFavorite();
  const { mutateAsync: removeFavorite } = useRemoveCaseFromFavorite();
  const { resolveSubdivisionName } = useCountriesData();
  const [favorite, setFavorite] = useState(isFavourite);
  const caseSuffixes = getCaseSuffixes(
    organType,
    reAllocationSequenceNumber,
    opoRequest,
    isExpeditedCase,
    laterality,
    duplicateCaseNumber,
  );
  const getState = (location?: Location) => {
    const { country, state } = location ?? {};
    if (!state) return undefined;

    return resolveSubdivisionName(state, country);
  };

  const onFavoriteClick = () => {
    if (isCandidateCard) return;
    if (isFavourite) removeFavorite({ caseId });
    else addFavorite({ caseId });

    setFavorite((fav) => !fav);
  };

  return (
    <>
      <div className="flex justify-between items-center w-full">
        <div>
          <Chip className={getColorByStatus(status)}>
            <span className="text-s-sb">{getCaseStatusName(status, randomization?.result)}</span>
          </Chip>
        </div>
        {!isReadOnly && (
          <div className="flex gap-2 items-center">
            <IconButton
              onPress={onFavoriteClick}
              isDisabled={isCandidateCard}
              className="h-auto w-auto min-w-0 p-1"
              variant="light"
              startContent={
                favorite ? (
                  <MdStar aria-label="In Favorite" size={24} />
                ) : (
                  <MdStarOutline aria-label="Not Favorite" size={24} />
                )
              }
            />
            <BaseCardActions
              caseData={caseData}
              isCandidateCard={isCandidateCard}
              selectType={selectType}
              openResources={openResources}
            />
          </div>
        )}
      </div>
      <CardTitle
        donorId={donorId}
        caseSuffixes={caseSuffixes}
        organType={organType}
        donorType={donorType}
        color={color}
        dateLabel={dateLabel}
        dateValue={dateValue}
        serviceType={serviceType}
      />
      <div className="flex flex-col w-full gap-1">
        <CardHospital
          isEmptyHospital={!donorHospital?.id}
          image={donorHospital?.logoPath}
          name={donorHospital?.name || 'Donor Hospital'}
          code={donorHospital?.code}
          state={getState(donorHospital?.location)}
          withLine={false}
        />
        <CardHospital
          isEmptyHospital={!recipientHospital?.id}
          image={recipientHospital?.logoPath}
          name={recipientHospital?.name || 'TX center'}
          code={recipientHospital?.code}
          state={getState(recipientHospital?.location)}
        />
      </div>
    </>
  );
};
